// The "fileUpload" widget of the row form (backend operate/row_schema.py): a file column with one
// file name per cell. Choosing a file uploads it at once (POST .../tables/{table}/files?column=,
// raw body with its MD5; backend routers/data_files.py) and puts its name into the field; the
// form then sends {"upload": <object id>} for it (RowForm.jsx, liveFiles.withUploads). The name
// can also be typed: a file the database has already is linked by its name.
//
// Accessible: the file input has its own label, the progress is a <progress> with a text twin,
// an error is announced (role="alert"), everything works with the keyboard (native inputs).
import { useId, useState } from "react";
import { api } from "../../api";
import { Input } from "../../components/Shared";
import { md5OfFile, uploadErrorText } from "../../components/UploadPanel";
import { fileSize, uploadPath } from "./liveFiles";

const noteStyle = { fontSize: 12, color: "#4b5563", marginTop: 4 };

export default function FileUploadWidget({ id, value, onChange, options, disabled, readonly, required, registry, label }) {
  const file = options.file || {};
  const ctx = registry.formContext || {};
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);
  const hint = useId();
  const accept = (file.extensions || []).join(",") || undefined;

  const choose = async e => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!f) return;
    setError(null);
    setDone(null);
    if (f.size === 0) {
      setError(`${f.name} is empty (0 bytes); choose the file itself.`);
      return;
    }
    setProgress(0);
    try {
      const md5 = await md5OfFile(f);
      const up = await api.upload(uploadPath(ctx.projectId, ctx.table, file.column || id), f,
                                  { md5, onProgress: p => setProgress(Math.round(p * 100)) });
      ctx.onUpload && ctx.onUpload(file.column, up);
      setDone(up);
      onChange(up.filename);
    } catch (err) {
      setError(uploadErrorText(err));
    } finally {
      setProgress(null);
    }
  };

  const off = disabled || readonly;
  return (
    <div>
      <Input id={id} value={value == null ? "" : value} disabled={disabled} readOnly={readonly}
             required={required} aria-required={required || undefined} aria-label={label}
             aria-describedby={hint} autoComplete="off"
             onChange={e => onChange(e.target.value === "" ? undefined : e.target.value)} />
      {!off && (
        <div style={{ marginTop: 6, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <label htmlFor={`${id}-file`} style={{ fontSize: 13, fontWeight: 600 }}>Upload a file for {label}</label>
          <input id={`${id}-file`} type="file" accept={accept} onChange={choose} disabled={progress !== null}
                 style={{ fontSize: 13 }} />
        </div>
      )}
      {progress !== null && (
        <div style={noteStyle}>
          <progress max={100} value={progress} aria-label={`Uploading the file for ${label}`} />{" "}
          <span role="status">Uploading… {progress}%</span>
        </div>
      )}
      {done && (
        <div role="status" style={noteStyle}>
          Uploaded {done.filename} ({fileSize(done.size)}, checksum <code>{done.checksum}</code>); save the row to link it.
        </div>
      )}
      <div id={hint} style={noteStyle}>
        {(file.extensions || []).length ? `A ${file.extensions.join(" or ")} file. ` : ""}
        Upload it, or type the name of a file the database has already.
      </div>
      {error && <div role="alert" style={{ ...noteStyle, color: "#b91c1c" }}>{error}</div>}
    </div>
  );
}
